import React from 'react';
import { Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../utils/currency';

/**
 * One truth on the PZ card of an invoice receipt: the batch value today
 * („Wartość partii”, the figure the batch, WZ and „Fracht w wartości partii”
 * use; it moves with freight edits) and, only when it differs, the accepted
 * cost on the day of receipt (immutable snapshot, backend utils/pzReceiptCost.js).
 * Without a current value (no batch yet) the accepted cost keeps its label.
 */
export default function PZReceiptValue({
  acceptedReceiptCostPLN,
  currentBatchValuePLN,
  layout = 'inline',
}: {
  acceptedReceiptCostPLN?: number;
  currentBatchValuePLN?: number;
  /** `field`: label above value (item card); `cell`: value only, the column
   *  header names it (items table); `inline`: „Etykieta: wartość” (totals). */
  layout?: 'inline' | 'field' | 'cell';
}) {
  const { t } = useTranslation(['pz']);
  if (currentBatchValuePLN == null && acceptedReceiptCostPLN == null) return null;
  const current = currentBatchValuePLN != null;
  const label = t(current ? 'pz:fields.currentBatchValue' : 'pz:fields.acceptedReceiptCost');
  const value = formatCurrency((current ? currentBatchValuePLN : acceptedReceiptCostPLN) as number, 'PLN');
  const original = current && acceptedReceiptCostPLN != null && acceptedReceiptCostPLN !== currentBatchValuePLN
    ? `${t('pz:fields.acceptedOnReceiptDay')}: ${formatCurrency(acceptedReceiptCostPLN, 'PLN')}`
    : null;
  if (layout === 'cell') {
    return (
      <span className="doc-item" data-testid="pz-item-receipt-value">
        <span className="doc-amount">{value}</span>
        {original && <span className="doc-item__meta">{original}</span>}
      </span>
    );
  }
  if (layout === 'field') {
    return (
      <div className="pz-item-field" data-testid="pz-item-receipt-value">
        <Typography.Text className="pz-item-field__label">{label}</Typography.Text>
        <Typography.Text strong>{value}</Typography.Text>
        {original && <Typography.Text type="secondary">{original}</Typography.Text>}
      </div>
    );
  }
  return (
    <Typography.Text data-testid="pz-receipt-value">
      {label}: <strong>{value}</strong>
      {original && <Typography.Text type="secondary"> ({original})</Typography.Text>}
    </Typography.Text>
  );
}
